/**
 * DoneForYou
 *
 * Contact/scheduling page for the DFY service. Authenticated users can submit
 * a message to admins or schedule a call via Calendly.
 */
import React, { useState, useEffect } from 'react';
import { Calendar, MessageSquare, ArrowRight, Send, Phone } from 'lucide-react';
import { useAuth } from '../../context/auth';
import { SERVER_URL } from '../../api/core';
import { auth } from '../../firebase/firebase';
import { useToast } from '../../components/ui/notifications/toast-notification.jsx';
import Footer from '../../components/shared/Footer';
import { ButtonSpinner } from '../../components/ui/feedback/loading-spinner.jsx';
import { ErrorMessage } from '../../components/ui/feedback/error-message.jsx';
import { Textarea } from '../../components/ui/inputs/textarea';
import { Label } from '../../components/ui/inputs/label';
import { calendlySchedulingUrl, formFields, pageContent } from '../../data/content/doneForYouData';
import { textSupport, sendTextCalendlyUrl, gotvSiteUrl } from '../../data/content/textSupportData';
import api from '../../api';
import SEOHead from '../../components/shared/SEOHead';

function DoneForYou() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('contact');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [submitStatus, setSubmitStatus] = useState(null);
  const { addToast } = useToast();

  // Clear success message after a few seconds
  useEffect(() => {
    let timer;
    if (submitStatus && submitStatus.success) {
      timer = setTimeout(() => {
        setSubmitStatus(null);
      }, 5000);
    }
    return () => clearTimeout(timer);
  }, [submitStatus]);

  const handleChange = (e) => {
    setMessage(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError('');
    setSubmitStatus(null);

    try {
      // Get the current user's ID token for authentication
      const idToken = await auth.currentUser.getIdToken();
      
      const response = await fetch(`${SERVER_URL}/contact/done-for-you`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ message })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || 'Failed to send message');
      }

      const data = await response.json();
      setSubmitStatus({ 
        success: true, 
        message: data.message || pageContent.successMessage 
      });
      setMessage('');

      addToast(pageContent.successMessage, "success");
    } catch (error) {
      console.error('Error submitting form:', error);
      const errorMessage = error.message || 'Failed to submit. Please try again.';
      setError(errorMessage);
      setSubmitStatus({ 
        success: false, 
        message: errorMessage 
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="p-4 sm:p-6 pb-20 w-full max-w-[1600px] mx-auto">
      <SEOHead pageType="app-done-for-you" />
      {/* Page header */}
      <div className="mb-8">
        <div className="flex items-center gap-3 mb-2">
          <h1 className="text-2xl font-semibold text-gray-900 dark:text-gray-100">
            {pageContent.title}
          </h1>
          <div className="flex items-center gap-1 px-2 py-0.5 bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300 border border-purple-200 dark:border-purple-800/40 rounded-full text-xs font-medium">
            No Subscription Required
          </div>
        </div>
        <p className="text-gray-600 dark:text-gray-400">
          Get professional services from our team of experts who will handle your campaign creation from start to finish.
        </p>
      </div>

      {/* Service Overview - 4 Summary Boxes */}
      <div className="mb-8 bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-1">What We Offer</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-6">
          Full-service campaign solutions from creative to outreach.
        </p>
        
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          {/* Box 1: Creative & Design */}
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 hover:shadow-sm">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Creative & Design</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Professional ad creative, copywriting, and multi-format deliverables ready for Meta, Google, and programmatic platforms.
            </p>
          </div>

          {/* Box 2: Texting & Voice */}
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 hover:shadow-sm">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Texting & Voice</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              FCC-compliant P2P texting, MMS support, two-way messaging, RoboCalls, and instant voter polling through RoboCent.
            </p>
          </div>

          {/* Box 3: Voter Data */}
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 hover:shadow-sm">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Voter Data & Targeting</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Updated voter files, demographic appends, carrier lookups, and custom list segmentation for precise targeting.
            </p>
          </div>

          {/* Box 4: Full Support */}
          <div className="p-4 rounded-lg bg-gray-50 dark:bg-gray-800/50 border border-gray-200 dark:border-gray-700 transition-all duration-200 hover:border-purple-300 dark:hover:border-purple-700 hover:bg-purple-50/50 dark:hover:bg-purple-900/10 hover:shadow-sm">
            <h3 className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-2">Full Support & Compliance</h3>
            <p className="text-xs text-gray-600 dark:text-gray-400 leading-relaxed">
              Dedicated account support, FCC compliance, 10DLC registration, and proactive guidance from political experts.
            </p>
          </div>
        </div>
      </div>

      {/* Live support - section 1 */}
      <div className="mb-6 bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-1">{textSupport.phoneLabel}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Talk to our team for real-time help planning and executing a DFY campaign.
        </p>

        <a
          href={`tel:${textSupport.phoneNumberHref}`}
          className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-6 py-3 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
          onClick={() => { api.activity?.trackClick('dfyLiveSupportClicks'); }}
        >
          <Phone className="h-5 w-5 mr-2" />
          {textSupport.phoneNumberDisplay}
        </a>

        <div className="mt-3 text-xs text-gray-500 dark:text-gray-400">Available 24/7</div>
      </div>

      {/* Prefer to schedule - section 2 */}
      <div className="mb-8 bg-white dark:bg-transparent rounded-lg border border-gray-200 dark:border-gray-700 shadow-sm p-6">
        <h2 className="text-lg font-medium text-gray-900 dark:text-white mb-1">{textSupport.scheduleTitle}</h2>
        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          We'll scope your DFY needs and next steps in 15 minutes.
        </p>

        <div className="flex flex-col sm:flex-row gap-3">
          <a
            href={sendTextCalendlyUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 active:from-purple-800 active:to-indigo-800 px-6 py-3 text-sm font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-purple-500"
            onClick={() => { api.activity?.trackClick('dfyBookCalendlyClicks'); }}
          >
            <Calendar className="h-5 w-5 mr-2" />
            {textSupport.scheduleCta}
          </a>

          <a
            href={textSupport.partnerUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center rounded-md border border-gray-200 dark:border-gray-700 px-6 py-3 text-sm font-medium text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800"
            onClick={() => { api.activity?.trackClick('dfyVisitRobocentClicks'); }}
          >
            <img src="/images/logos/robocent.png" alt="Robocent" className="h-4 w-4 mr-2 object-contain" />
            {textSupport.partnerTitle}
          </a>
        </div>

        <div className="mt-5">
          <a
            href={gotvSiteUrl}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center text-sm px-1.5 py-1 rounded-md text-gray-700 dark:text-gray-300 transition-all duration-200 ease-out hover:text-purple-700 dark:hover:text-purple-200 hover:bg-purple-50 dark:hover:bg-purple-900/20 hover:ring-1 hover:ring-purple-200 dark:hover:ring-purple-800/40 hover:shadow-sm"
          >
            Advocacy Lab is now part of RoboCent / GOTV.com — click here to learn more
          </a>
        </div>
      </div>
      
      <Footer />
    </div>
  );
}

export default DoneForYou; 